import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import { t } from 'ttag'
import { redirect } from 'redux-first-router'
import { updateProjectTemplate } from 'api/projectTemplates'
import { mapInternalStateToPayload as mapJobNameAssignmentStateToPayload } from 'components/modals/common/JobNameAssignmentModal/actions'
import { mapInternalStateToPayload as mapJobPositionStateToPayload } from 'components/modals/common/JobPositionModal/actions'
import { DEFAULT_JOB_TEMPLATES_QUERY_PARAMS } from 'constants/templates'
import { getLocation, toTemplates, toProjects } from 'ducks/router'
import { useLanguage, useModal, useSnackbar } from 'hooks'
import { getOrm, jobTemplates, jobs } from 'orm'

const useQueries = () => {
  const dispatch = useDispatch()

  const handleGetJobTemplate = React.useCallback(
    ({ id }) => {
      dispatch(jobTemplates.fetchStart({ pk: id }))
    },
    [dispatch],
  )

  return {
    handleGetJobTemplate,
  }
}

const useMutations = () => {
  const dispatch = useDispatch()

  const handlePostJobPosition = React.useCallback(
    ({ data }, { onFailureAction, postSuccessAction }) => {
      dispatch(jobs.postStart({ data, onFailureAction, postSuccessAction }))
    },
    [dispatch],
  )

  const handlePatchJobTemplate = async (
    id,
    {
      category,
      description,
      languages,
      interview_template,
      modules,
      name,
      profile,
    },
  ) =>
    await updateProjectTemplate(id, {
      category,
      description,
      languages,
      interview_template,
      modules,
      name,
      profile,
    })

  return {
    handlePatchJobTemplate,
    handlePostJobPosition,
  }
}

const useData = handlers => {
  const {
    payload: { id },
  } = useSelector(getLocation)
  const template = useSelector(jobTemplates.getItem(id))
  const { isPending } = useSelector(getOrm)
  const language = useLanguage()

  const { handleGetJobTemplate } = handlers

  const { profile } = template ?? {}

  const hasCategories = R.pipe(
    R.values,
    R.any(R.equals(true)),
  )(profile?.categories)

  React.useEffect(() => {
    handleGetJobTemplate({ id })
  }, [id, handleGetJobTemplate])

  return {
    state: {
      language,
      template,
      hasCategories,
      isLoading: isPending,
    },
  }
}

const useEditJobTemplateModal = (state, handlers) => {
  const { template } = state
  const {
    handleGetJobTemplate,
    handlePatchJobTemplate,
    handleShowSnackbar,
  } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleConfirm = React.useCallback(
    async (data, { onError, onSuccess }) => {
      const payload = mapJobPositionStateToPayload(data)

      const { error, meta, response } = await handlePatchJobTemplate(
        template?.id,
        payload,
      )

      if (response) {
        handleClose()
        onSuccess()
        handleShowSnackbar({
          message: t`Job template was successfully updated.`,
          type: 'success',
        })
        handleGetJobTemplate({ id: template?.id })
      } else {
        if (onError) {
          onError({ message: error, type: meta.HTTPstatus })
        }
      }
    },
    [
      template,
      handleClose,
      handleGetJobTemplate,
      handlePatchJobTemplate,
      handleShowSnackbar,
    ],
  )

  return {
    handleEditJobTemplateClose: handleClose,
    handleEditJobTemplateOpen: handleOpen,
    handleEditJobTemplateConfirm: handleConfirm,
    isEditJobTemplateOpen: isOpen,
  }
}

const useTemplateBasedJobPositionModal = (state, handlers) => {
  const dispatch = useDispatch()

  const { template } = state
  const { handlePostJobPosition } = handlers

  const { handleClose, handleOpen, isOpen } = useModal()

  const handleConfirm = React.useCallback(
    ({ title }, { onFailureAction }) => {
      const data = mapJobNameAssignmentStateToPayload({ ...template, title })

      handlePostJobPosition(
        { data },
        {
          onFailureAction,
          postSuccessAction: response => {
            handleClose()
            dispatch(redirect(toProjects(response.id, 'details')))
            // TODO: uncomment when we clean up the code
            // for snackbar handling in the sagas
            // handleShowSnackbar({ message: t`Job position was successfully created.`, type: 'success' })
          },
        },
      )
    },
    [template, handleClose, handlePostJobPosition],
  )

  return {
    handleTemplateBasedJobPositionClose: handleClose,
    handleTemplateBasedJobPositionOpen: handleOpen,
    handleTemplateBasedJobPositionConfirm: handleConfirm,
    isTemplateBasedJobPositionOpen: isOpen,
  }
}

export const useJobTemplateDetails = () => {
  const dispatch = useDispatch()

  const {
    handlers: { handleShowSnackbar },
  } = useSnackbar()

  const { handleGetJobTemplate } = useQueries()

  const { handlePatchJobTemplate, handlePostJobPosition } = useMutations()

  const {
    state: { language, template, hasCategories, isLoading },
  } = useData({ handleGetJobTemplate })

  const {
    handleEditJobTemplateClose,
    handleEditJobTemplateConfirm,
    handleEditJobTemplateOpen,
    isEditJobTemplateOpen,
  } = useEditJobTemplateModal(
    { template },
    { handleGetJobTemplate, handlePatchJobTemplate, handleShowSnackbar },
  )

  const {
    handleTemplateBasedJobPositionClose,
    handleTemplateBasedJobPositionOpen,
    handleTemplateBasedJobPositionConfirm,
    isTemplateBasedJobPositionOpen,
  } = useTemplateBasedJobPositionModal({ template }, { handlePostJobPosition })

  const handleBack = React.useCallback(() => {
    dispatch(toTemplates('jobs', null, DEFAULT_JOB_TEMPLATES_QUERY_PARAMS))
  }, [dispatch])

  return {
    state: {
      language,
      template,
      hasCategories,
      isLoading,
      isEditJobTemplateOpen,
      isTemplateBasedJobPositionOpen,
    },
    handlers: {
      handleBack,
      handleEditJobTemplateClose,
      handleEditJobTemplateConfirm,
      handleEditJobTemplateOpen,
      handleTemplateBasedJobPositionClose,
      handleTemplateBasedJobPositionOpen,
      handleTemplateBasedJobPositionConfirm,
    },
  }
}
